import React, { useEffect, useRef, useState } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   Tabs,
   Tab,
   Box,
   Typography,
   TextField,
   CircularProgress,
   Alert,
   LinearProgress,
   FormControlLabel,
   Switch,
   Grid,
   Link,
   CardMedia,
   Card,
   CardContent,
   CardActionArea,
} from '@mui/material';
import { Google as GoogleIcon, Apple as AppleIcon } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';

interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

const TabPanel = (props: TabPanelProps) => {
   const { children, value, index, ...other } = props;

   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`sync-tabpanel-${index}`}
         aria-labelledby={`sync-tab-${index}`}
         {...other}
      >
         {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
      </div>
   );
};

interface PhotoSyncDialogProps {
   open: boolean;
   onClose: () => void;
   tabValue: number;
   onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
   onGooglePhotosSync: () => void;
   onICloudSync: (username: string, password: string, verificationCode?: string) => Promise<void>;
   syncInProgress?: boolean;
   syncError?: string | null;
   syncStatus?: string;
   isShowQrCode?: boolean;
   qrCode?: string;
   selectedPickerPhotos?: any[];
   resetSelectedPickerPhotos?: () => void;
   photoUploadUrl?: string;
   isSyncing?: boolean;
   handleDeletePhoto?: (id: string) => void;
   handleSyncPhotoUpload?: () => void;
   isUploading?: boolean;
}

const PhotoSyncDialog: React.FC<PhotoSyncDialogProps> = ({
   open,
   onClose,
   tabValue,
   onTabChange,
   onGooglePhotosSync,
   onICloudSync,
   syncInProgress,
   syncError,
   syncStatus,
   isShowQrCode,
   qrCode,
   selectedPickerPhotos = [],
   resetSelectedPickerPhotos,
   photoUploadUrl,
   isSyncing = false,
   handleDeletePhoto,
   handleSyncPhotoUpload,
   isUploading = false,
}) => {
   const [autoSync, setAutoSync] = useState(false);
   const [iCloudUsername, setICloudUsername] = useState('');
   const [iCloudPassword, setICloudPassword] = useState('');
   const [verificationCode, setVerificationCode] = useState('');
   const [needsVerification, setNeedsVerification] = useState(false);

   const formattedDate = (date: string) => {
      const dateObj = new Date(date || '');
      return isNaN(dateObj.getTime())
         ? ''
         : dateObj.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
           });
   };

   return (
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
         <DialogTitle>Sync Photos from Cloud Services</DialogTitle>
         <DialogContent>
            <Tabs
               value={tabValue}
               onChange={onTabChange}
               aria-label='photo sync tabs'
               variant='fullWidth'
               sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
               <Tab
                  icon={<GoogleIcon />}
                  label='Google Photos'
                  id='sync-tab-0'
                  aria-controls='sync-tabpanel-0'
               />
               {/* <Tab
                  icon={<AppleIcon />}
                  label='iCloud Photos'
                  id='sync-tab-1'
                  aria-controls='sync-tabpanel-1'
               /> */}
            </Tabs>

            {syncError && (
               <Alert severity='error' sx={{ mb: 2 }}>
                  {syncError}
               </Alert>
            )}

            {syncStatus && !syncError && (
               <Alert severity='info' sx={{ mb: 2 }}>
                  {syncStatus}
               </Alert>
            )}

            {syncInProgress && <LinearProgress sx={{ mb: 2 }} />}

            <TabPanel value={tabValue} index={0}>
               {isShowQrCode || selectedPickerPhotos.length > 0 ? (
                  selectedPickerPhotos.length > 0 ? (
                     <Grid container spacing={2}>
                        <Grid
                           item
                           xs={12}
                           sm={12}
                           md={12}
                           style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 10,
                           }}
                        >
                           {/* {selectedPickerPhotos.length > 10 && (
                              <Alert severity='error'>
                                 You have selected more than 10 photos. Please select only 10
                                 photos.
                              </Alert>
                           )} */}
                           {isSyncing && <CircularProgress color='primary' />}
                           {selectedPickerPhotos.length > 0 && isSyncing && (
                              <Alert severity='info'>
                                 Please wait while we are syncing your selected photos. Do not close
                                 or refresh the page.
                              </Alert>
                           )}
                           {/* <Button
                              color='primary'
                              style={{ marginBottom: '10px', height: '40px' }}
                              onClick={resetSelectedPickerPhotos}
                           >
                              Reset
                           </Button> */}
                        </Grid>
                        {selectedPickerPhotos.map((photo) => (
                           <Grid item xs={12} sm={6} md={3} key={photo?.id}>
                              <Card>
                                 <CardActionArea>
                                    <CardMedia
                                       component='img'
                                       image={photo?.baseUrl}
                                       alt={photo?.filename}
                                       sx={{ width: '100%', height: '200px' }}
                                    />
                                    <Button
                                       onClick={() => {
                                          if (handleDeletePhoto) {
                                             handleDeletePhoto(photo?.id);
                                          }
                                       }}
                                       sx={{ position: 'absolute', top: 0, right: 0 }}
                                    >
                                       <DeleteIcon />
                                    </Button>
                                 </CardActionArea>
                                 <CardContent>
                                    <Typography
                                       sx={{ wordWrap: 'break-word' }}
                                       variant='body2'
                                       paragraph
                                    >
                                       {photo?.filename?.slice(0, 20)}
                                    </Typography>

                                    <Typography variant='body2' paragraph>
                                       Date : {formattedDate(photo?.takenAt)}
                                    </Typography>

                                    {photo?.progress && (
                                       <LinearProgress
                                          variant='determinate'
                                          value={photo.progress}
                                       />
                                    )}
                                 </CardContent>
                              </Card>
                           </Grid>
                        ))}
                     </Grid>
                  ) : (
                     <Box
                        sx={{
                           display: 'flex',
                           justifyContent: 'center',
                           mb: 2,
                           flexDirection: 'column',
                           alignItems: 'center',
                        }}
                     >
                        <img
                           src={qrCode}
                           alt='QR Code'
                           width='200px'
                           height='200px'
                           style={{ marginBottom: '10px' }}
                        />
                        <Typography paragraph sx={{ mb: 2 }}>
                           Scan the QR code or continue with open tab to select photos from your
                           Google Photos.
                        </Typography>

                        <Button
                           style={{ marginBottom: '10px' }}
                           color='primary'
                           onClick={onGooglePhotosSync}
                        >
                           <Link target='_blank' href={photoUploadUrl}>
                              Open on web
                           </Link>
                        </Button>
                        {isSyncing && <CircularProgress />}
                     </Box>
                  )
               ) : (
                  <>
                     <Typography paragraph>
                        Connect to your Google Photos account to select and import photos. You will
                        be redirected to Google to authorize access.
                     </Typography>

                     {/* <FormControlLabel
                        control={
                           <Switch
                              checked={autoSync}
                              onChange={(e) => setAutoSync(e.target.checked)}
                              disabled={syncInProgress}
                           />
                        }
                        label='Automatically sync new photos'
                        sx={{ mb: 2 }}
                     /> */}

                     <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button
                           sx={{ width: 'max-content' }}
                           variant='contained'
                           color='primary'
                           onClick={onGooglePhotosSync}
                           disabled={isSyncing}
                           fullWidth
                        >
                           {isSyncing ? 'Connecting...' : 'Connect to Google Photos'}
                           {isSyncing && <CircularProgress size={20} sx={{ ml: 1 }} />}
                        </Button>
                     </Box>
                  </>
               )}
            </TabPanel>

            {/* <TabPanel value={tabValue} index={1}>
               <Typography paragraph>
                  Sign in to your iCloud account to access and import your photos. Your credentials
                  are securely transmitted and never stored.
               </Typography>

               <Box component='form' sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                     label='iCloud Email'
                     value={iCloudUsername}
                     onChange={(e) => setICloudUsername(e.target.value)}
                     disabled={syncInProgress}
                     fullWidth
                  />

                  <TextField
                     label='Password'
                     type='password'
                     value={iCloudPassword}
                     onChange={(e) => setICloudPassword(e.target.value)}
                     disabled={syncInProgress}
                     fullWidth
                  />

                  {needsVerification && (
                     <TextField
                        label='Verification Code'
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        disabled={syncInProgress}
                        placeholder='Enter the code sent to your device'
                        fullWidth
                     />
                  )}

                  <FormControlLabel
                     control={
                        <Switch
                           checked={autoSync}
                           onChange={(e) => setAutoSync(e.target.checked)}
                           disabled={syncInProgress}
                        />
                     }
                     label='Automatically sync new photos'
                     sx={{ mb: 1 }}
                  />

                  <Button
                     variant='contained'
                     color='primary'
                     onClick={() =>
                        onICloudSync(iCloudUsername, iCloudPassword, verificationCode || undefined)
                     }
                     disabled={
                        syncInProgress ||
                        !iCloudUsername ||
                        !iCloudPassword ||
                        (needsVerification && !verificationCode)
                     }
                     fullWidth
                  >
                     {syncInProgress ? 'Connecting...' : 'Connect to iCloud Photos'}
                     {syncInProgress && <CircularProgress size={20} sx={{ ml: 1 }} />}
                  </Button>
               </Box>
            </TabPanel> */}
         </DialogContent>
         <DialogActions>
            <Box sx={{ display: 'flex', gap: 2 }}>
               {/* {selectedPickerPhotos.length > 0 && selectedPickerPhotos.length <= 10 && (
                  <Button
                     onClick={() => {
                        if (handleSyncPhotoUpload) {
                           handleSyncPhotoUpload();
                        }
                     }}
                     variant='contained'
                     color='primary'
                  >
                     Upload
                  </Button>
               )} */}

               <Button onClick={onClose} color='primary'>
                  Close
               </Button>
            </Box>
         </DialogActions>
      </Dialog>
   );
};

export default PhotoSyncDialog;
